import { Dispatch, SetStateAction, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { IconButton, TableCell, TableRow, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { FormAutocompleteController, FormInputNumberController } from '@/components';
import {
  getContentLanguage,
  languageSchema,
  MenuRecipeUpdateForm,
  onQueryError,
  onQuerySuccess,
  Product,
  RecipeItem,
  useMenu,
  useProducts,
} from '@/core';

import { styles } from './styles';

const schema = z.object({
  product: z.object({
    id: z.number(),
    name: languageSchema,
  }),
  amount: z.number(),
});

export const EditableRecipeItem = ({
  recipeItem,
  setRecipeItems,
}: {
  recipeItem: RecipeItem;
  setRecipeItems: Dispatch<SetStateAction<RecipeItem[]>>;
}) => {
  const { t, i18n } = useTranslation();
  const { useProductsSearch } = useProducts({});
  const {
    queries: { useMenuRecipe },
    mutations: { updateMenuRecipeMutation, deleteMenuRecipeProductMutation },
  } = useMenu();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const [options, setOptions] = useState<Product[]>([]);
  const { data: productsResponse } = useMenuRecipe(Number(id), true);

  const methods = useForm<RecipeItem>({
    defaultValues: {
      product: recipeItem.product,
      amount: recipeItem.amount,
    },
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  const { getValues } = methods;

  const onSuccess = () => {
    onQuerySuccess({
      queryClient,
      queryKeysToInvalidate: [['menuRecipe', id]],
    });
  };

  const handleDeleteRecipeProductClick = async () => {
    const deleteResponse = await deleteMenuRecipeProductMutation.mutateAsync({
      menuId: Number(id),
      productId: recipeItem.product.id,
    });
    if (deleteResponse.status === 200) {
      setRecipeItems((prev) => prev.filter((i) => i.product.id !== recipeItem.product.id));
      onSuccess();
    } else {
      onQueryError({ errorMessage: t('MENU.RECIPE_ITEM_DELETE_FAILURE') });
    }
  };

  const handleSaveRecipeItem = () => {
    const values = getValues();
    if (!values.product.id) return;
    setRecipeItems((prev: RecipeItem[]) => {
      const updatedRecipeItems = prev.map((item) => {
        if (item.product.id === recipeItem.product.id) {
          return {
            product: values.product,
            amount: Number(values.amount),
          };
        }
        return item;
      });

      const updatedRecipeItemsApiForm: MenuRecipeUpdateForm[] = updatedRecipeItems
        .filter((i) => i.product !== undefined)
        .map((item) => ({
          product_id: item.product!.id,
          amount: Number(item.amount.toFixed(2)),
        }));

      updateMenuRecipeMutation.mutate(
        {
          menuId: Number(id),
          data: updatedRecipeItemsApiForm,
        },
        { onSuccess },
      );

      return updatedRecipeItems;
    });
  };

  return (
    <FormProvider {...methods}>
      <TableRow>
        <TableCell align="left">
          <FormAutocompleteController<Product>
            label={t('PRODUCT.CHOOSE')}
            controllerName={'product'}
            options={options}
            fetchOptions={useProductsSearch}
            setOptions={setOptions}
            getOptionLabel={(option) => option.name[getContentLanguage(i18n.language)] || ''}
            getOptionKey={(option) => option.id.toString()}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            fieldOnBlur={handleSaveRecipeItem}
            renderInput={() => <div></div>}
            optionsToFilterOut={productsResponse?.data?.map((p) => p.product as Product) || []}
          />
        </TableCell>
        <TableCell align="left">
          <FormInputNumberController
            label={''}
            controllerName={'amount'}
            fieldOnBlur={handleSaveRecipeItem}
            isWholeNumber
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={styles.shrinkCell}>
            {t('GLOBAL.PIECES')}
          </Typography>
        </TableCell>
        <TableCell align="right" sx={styles.shrinkCell}>
          <IconButton size="medium" onClick={handleDeleteRecipeProductClick}>
            <span className="material-symbols-rounded">close</span>
          </IconButton>
        </TableCell>
      </TableRow>
    </FormProvider>
  );
};
